import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useHrStaffAccountsQuery, useImportStaffMutation } from '@/client/api/hr/people';

/** Create employee records for FeelyFeely accounts already marked staff or
 *  given console access. Nothing is ticked by default: test and duplicate
 *  accounts carry the same flags, so HR picks who is really an employee. */
export function ImportStaffDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const accountsQ = useHrStaffAccountsQuery(open);
  const importStaff = useImportStaffMutation();
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const accounts = accountsQ.data ?? [];

  const toggle = (uid: number) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });

  const close = () => {
    setPicked(new Set());
    onOpenChange(false);
  };

  const submit = () => {
    importStaff.mutate([...picked], {
      onSuccess: (res) => {
        const n = res.created?.length ?? 0;
        toast.success(`Imported ${n} ${n === 1 ? 'employee' : 'employees'}`, {
          description: res.skipped?.length ? `${res.skipped.length} skipped (already linked or no longer staff).` : undefined,
        });
        close();
      },
      onError: (err) => toast.error((err as Error).message || 'Import failed'),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Import staff accounts</DialogTitle>
          <DialogDescription>
            FeelyFeely accounts marked as staff or with console access that are not in the directory yet. Tick
            the real employees; you can fill in title, department and manager afterwards.
          </DialogDescription>
        </DialogHeader>

        {accountsQ.isLoading ? (
          <p className="py-6 text-center text-sm text-gray-500">Loading…</p>
        ) : accountsQ.isError ? (
          <p className="py-6 text-center text-sm text-red-600">{(accountsQ.error as Error)?.message ?? 'Could not load accounts'}</p>
        ) : accounts.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">Every staff account is already in the directory.</p>
        ) : (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {accounts.map((a) => (
              <li key={a.uid}>
                <label className="flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    className="mt-1 accent-slate-900"
                    checked={picked.has(a.uid)}
                    onChange={() => toggle(a.uid)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-gray-900">{a.name || `uid ${a.uid}`}</span>
                    <span className="block truncate text-xs text-gray-500">
                      {a.email || 'No email'} · uid {a.uid}
                    </span>
                  </span>
                  <span className="flex shrink-0 flex-col items-end gap-1 text-[11px]">
                    {a.consoleAccess && <span className="rounded-full bg-green-100 px-2 py-0.5 text-green-800">Console</span>}
                    {!a.active && <span className="rounded-full bg-gray-200 px-2 py-0.5 text-gray-700">Inactive</span>}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button type="button" disabled={picked.size === 0 || importStaff.isPending} onClick={submit}>
            {importStaff.isPending ? 'Importing…' : `Import ${picked.size || ''}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
